/**
 * Zone Of Kinship (ZOK) Person Type Definitions
 *
 * Core person-related types for the WEPS ecosystem.
 * Unifies PWI and Family & Relationships app type definitions.
 */

import { PersonRelationship } from "./relationship.types";

export interface PersonName {
  firstName: string;
  middleName?: string;
  lastName: string;
  nameSuffix?: string;
  title?: string;
  preferredName?: string; // From F&R
}

export interface BeneficiaryReference {
  beneId: string;
  beneSetId: string;
  accountId: string;
}

/**
 * ProducerSource identifies the application or flow that created a HouseholdMember.
 * Used to distinguish client-facing manual entries from system-generated records.
 */
export type ProducerSource = "PWI" | "F&R" | "VanillaBatch";

/**
 * ALLOWED_CLIENT_SOURCES defines the allow-list of producerSource values that
 * should be shown to clients. This is a safelist — any source not in this list
 * is hidden by default, including promoted beneficiaries, VWS beneficiaries,
 * Client Groups, VanillaBatch, and any future sources.
 *
 * When a new source becomes client-ready, add it to this list explicitly.
 */
export const ALLOWED_CLIENT_SOURCES: ReadonlyArray<ProducerSource> = ["PWI", "F&R"] as const;

export interface MemberReferences {
  clientPoid?: string;
  fo5MemberId?: string;
  modelId?: string;
  beneficiaryRefs?: BeneficiaryReference[];
  vwsBeneficiaryRefs?: BeneficiaryReference[];
  isTrustSettlor?: boolean; // From F&R
}

export interface LastUpdatedBy {
  id?: string;
  idType?: "CLIENT" | "ASSOCIATE" | "PROCESS";
  correlationCallChain?: string;
  timestamp?: string; // From PWI (keep for backward compatibility)
}

export interface Person {
  householdMemberId: string;
  name: PersonName;
  birthDate: string; // Format: "YYYY-MM-DD"
  producerSource?: ProducerSource; // Identifies who/what created this person (top-level field)
  referenceIds?: MemberReferences;
  state?: string;
  email?: string;
  isPrimary?: boolean;
  needsReview?: boolean;
  deceased?: boolean;
  relationships?: PersonRelationship[]; // Use F&R's nested structure
  lastUpdatedBy: LastUpdatedBy;
}

export interface PersonWithStatus extends Person {
  status: "created" | "updated" | "unchanged";
}

export function getPersonDisplayName(person: Person): string {
  const preferred = person.name.preferredName?.trim();
  if (preferred) return preferred;
  const first = person.name.firstName.trim();
  const last = person.name.lastName.trim();
  return `${first} ${last}`.trim();
}
